/**
 * The analytics event allowlist — the single definition of what the app is
 * allowed to record (docs/analytics.md is the human-readable copy of this table).
 *
 * Used by netlify/functions/event.js to sanitise every incoming batch and by the
 * story function to record its own `story_served` metric. Anything not listed
 * here — an unknown event name, an unknown prop, a wrong type, an over-long
 * string — is silently dropped before it is stored. There is no free text: the
 * only strings that are not enums or slugs are two normalised, lowercase,
 * alphabetic, 40-character fields (`search.query` and `animal_requested.name`),
 * and both are re-normalised server-side.
 *
 * Deliberately NOT accepted anywhere: names, IP addresses, user-agent strings,
 * headers, URLs, story text, or anything typed by a child other than the two
 * fields above. The device and session ids are random strings the client made up.
 */

export const BATCH_VERSION = 1
export const MAX_EVENTS_PER_BATCH = 25

export const AGES = ['2', '4', '6']
// 'loading' is the pre-D-099 interstitial; it stays accepted for clients that have not reloaded.
export const SCREENS = ['age', 'roster', 'loading', 'rumble', 'matchup', 'facts', 'battle', 'reveal', 'quiz', 'collection', 'credits']
export const DEVICE_CLASSES = ['ipad', 'iphone', 'android', 'desktop', 'other']
export const SPEECH_ERRORS = ['denied', 'no-speech', 'unsupported', 'error']
export const STORY_SOURCES = ['device', 'server']
// Which path a share took (D-145): the OS share sheet, the clipboard, or the
// address shown on screen because neither was available.
export const SHARE_METHODS = ['native', 'copy', 'link']

const SLUG_RE = /^[a-z][a-z0-9-]{0,39}$/
const ID_RE = /^[a-z0-9]{8,32}$/
const VERSION_RE = /^[a-z0-9]{1,16}$/
const TOKEN_RE = /^[a-z][a-z0-9_-]{0,31}$/

/* ---------- field types ---------- */

const bool = () => (v) => (typeof v === 'boolean' ? v : undefined)
const int = (min, max) => (v) => (Number.isInteger(v) && v >= min && v <= max ? v : undefined)
const oneOf = (list) => (v) => (typeof v === 'string' && list.includes(v) ? v : undefined)
const re = (rx) => (v) => (typeof v === 'string' && rx.test(v) ? v : undefined)
const slug = re(SLUG_RE)
const age = oneOf(AGES)

/** Lowercase letters and single spaces only, 40 chars max. Same rule client-side. */
export function normalizeText(v) {
  if (typeof v !== 'string') return ''
  return v
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '') // "elephant" with an accent -> "elephant", not "e lephant"
    .replace(/[^a-z ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 40)
    .trim()
}
const text = () => (v) => {
  const t = normalizeText(v)
  return t.length >= 1 ? t : undefined
}

/* ---------- the allowlist ---------- */

/**
 * name -> { props: { key: coerce(v) -> value|undefined }, required?: [keys], server?: true }
 * A `server` event is only accepted from the story function, never from a client.
 */
export const EVENTS = {
  screen_view: { props: { screen: oneOf(SCREENS) }, required: ['screen'] },
  age_set: { props: { age }, required: ['age'] },

  pick: { props: { slug, side: oneOf(['a', 'b']) }, required: ['slug', 'side'] },
  unpick: { props: { slug }, required: ['slug'] },
  surprise: { props: {} },
  // `query` is only kept when the search found nothing — that is the "what do
  // kids want that we don't have" signal. A search that found something sends
  // just the count.
  search: { props: { results: int(0, 10000), query: text() }, required: ['results'] },
  animal_requested: { props: { name: text() }, required: ['name'] },

  speech_start: { props: {} },
  speech_result: { props: { ok: bool() }, required: ['ok'] },
  speech_error: { props: { reason: oneOf(SPEECH_ERRORS) }, required: ['reason'] },

  story_requested: { props: { a: slug, b: slug, age }, required: ['age'] },
  story_ready: {
    props: { a: slug, b: slug, age, ms: int(0, 600000), source: oneOf(STORY_SOURCES), fallback: bool() },
    required: ['a', 'b', 'age', 'ms', 'source'],
  },
  story_error: { props: { status: int(0, 599) }, required: ['status'] },

  fight: { props: {} },
  facts_done: { props: { pages: int(0, 20) }, required: ['pages'] },
  battle_done: { props: { beats: int(0, 20) }, required: ['beats'] },
  battle_complete: { props: { a: slug, b: slug, winner: slug, age }, required: ['a', 'b', 'winner', 'age'] },

  quiz_answer: { props: { i: int(0, 20), correct: bool(), first_try: bool() }, required: ['i', 'correct', 'first_try'] },
  quiz_done: { props: { score: int(0, 20), of: int(1, 20) }, required: ['score', 'of'] },
  quiz_skipped: { props: { at: int(0, 20) }, required: ['at'] },

  // `via` says whether the speaker button or the space bar (D-112) started it.
  readaloud_tap: { props: { via: oneOf(['tap', 'key']) } },
  mute: { props: { on: bool() }, required: ['on'] },
  rematch: { props: { slug }, required: ['slug'] },
  collection_reset: { props: {} },

  // Sharing a battle (D-145, docs/SHARE.md). `share_tap` is one tap on the
  // reveal's share button: which path ran and whether it finished. `ok: true` on
  // the native path only means the sheet closed without a cancel, not that a
  // message was sent. `shared_open` is the app opening from a battle link; `a`
  // and `b` are present when the link resolved to two roster animals, so a bare
  // `age` is the bad-link signal. The two can never be joined: a link carries
  // no id of any kind, by design — a per-share id would be a tracking token.
  share_tap: { props: { a: slug, b: slug, age, method: oneOf(SHARE_METHODS), ok: bool() }, required: ['a', 'b', 'age', 'method', 'ok'] },
  shared_open: { props: { a: slug, b: slug, age }, required: ['age'] },
  app_error: { props: { kind: re(TOKEN_RE) }, required: ['kind'] },

  // Server-side only: recorded by the story function after it serves a story.
  // `generated` means a live model call succeeded; the offline `mock` provider
  // counts as neither generated nor fallback, so a missing key shows up here.
  story_served: {
    server: true,
    props: {
      a: slug,
      b: slug,
      age,
      cached: bool(),
      fallback: bool(),
      generated: bool(),
      ms: int(0, 600000),
      provider: re(TOKEN_RE),
      attempts: int(0, 5),
      // The budget, not the model, decided it (D-179): a call aborted at the
      // budget, or a retry there was no room for. A fallback with `budget` is
      // the gateway's shape, not the provider's.
      budget: bool(),
    },
    required: ['a', 'b', 'age', 'cached', 'fallback', 'generated', 'ms'],
  },
}

export const EVENT_NAMES = Object.keys(EVENTS)

/** Fields sent once per batch by the client. All optional except the two ids. */
const COMMON = {
  device: re(ID_RE),
  session: re(ID_RE),
  age,
  vw: int(0, 20000),
  vh: int(0, 20000),
  standalone: bool(),
  device_class: oneOf(DEVICE_CLASSES),
  version: re(VERSION_RE),
}

function coerceProps(spec, raw) {
  if (raw === undefined || raw === null) raw = {}
  if (typeof raw !== 'object' || Array.isArray(raw)) return null
  const out = {}
  for (const [key, coerce] of Object.entries(spec.props)) {
    if (!(key in raw)) continue
    const v = coerce(raw[key])
    if (v !== undefined) out[key] = v
  }
  for (const key of spec.required || []) if (!(key in out)) return null
  return out
}

/**
 * Sanitise one event. Returns { name, props, t? } or null if it is not on the
 * allowlist / fails validation.
 */
export function sanitizeEvent(raw, { origin = 'client' } = {}) {
  if (!raw || typeof raw !== 'object') return null
  const name = raw.name
  const spec = EVENTS[name]
  if (!spec) return null
  if (spec.server && origin !== 'server') return null
  const props = coerceProps(spec, raw.props)
  if (!props) return null
  if (name === 'search' && props.results !== 0) delete props.query
  const out = { name, props }
  // Optional client clock: ms since page load, for ordering within a session.
  if (Number.isInteger(raw.t) && raw.t >= 0 && raw.t <= 1e9) out.t = raw.t
  return out
}

/**
 * Sanitise a whole batch: { common, events } -> stored batch, or null when the
 * envelope itself is malformed (which the function turns into a 4xx). Unknown
 * events and props inside a well-formed envelope are dropped silently.
 */
export function sanitizeBatch(body, { origin = 'client', now = Date.now() } = {}) {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null
  if (!Array.isArray(body.events)) return null
  const rawCommon = body.common ?? {}
  if (typeof rawCommon !== 'object' || Array.isArray(rawCommon)) return null

  const common = {}
  for (const [key, coerce] of Object.entries(COMMON)) {
    if (!(key in rawCommon)) continue
    const v = coerce(rawCommon[key])
    if (v !== undefined) common[key] = v
  }
  if (origin === 'client' && !(common.device && common.session)) return null

  const events = []
  for (const raw of body.events.slice(0, MAX_EVENTS_PER_BATCH)) {
    const e = sanitizeEvent(raw, { origin })
    if (e) events.push(e)
  }

  return {
    v: BATCH_VERSION,
    received_at: new Date(now).toISOString(),
    origin,
    common,
    events,
  }
}

export default { EVENTS, EVENT_NAMES, sanitizeBatch, sanitizeEvent, normalizeText }
